"use client";

import { useRouter } from "next/navigation";
import { FaCheck } from "react-icons/fa";

const CheckListItemDark = ({ text }: { text: string }) => (
  <li className="flex items-start gap-3">
    <span className="w-5 h-5 flex-shrink-0 rounded-full bg-purple-500 text-white flex items-center justify-center mt-0.5">
      <FaCheck size={9} />
    </span>
    <span className="text-purple-100 text-sm leading-relaxed">{text}</span>
  </li>
);

const CheckListItemLight = ({ text }: { text: string }) => (
  <li className="flex items-start gap-3">
    <span className="w-5 h-5 flex-shrink-0 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mt-0.5">
      <FaCheck size={9} />
    </span>
    <span className="text-gray-700 text-sm leading-relaxed">{text}</span>
  </li>
);

export default function TourGuidePage() {
  const router = useRouter();

  const whyBookFeatures = [
    "Licensed, experienced local tour guides.",
    "Personalized itineraries tailored to your interests.",
    "Deep knowledge of Sri Lankan history, culture, and nature.",
    "Flexible scheduling and private tours available.",
  ];

  const guideBenefits = [
    "Share your expertise and passion for Sri Lanka.",
    "Earn competitive income with flexible hours.",
    "Get featured on our platform to thousands of travelers.",
    "Dedicated support and marketing from Lily Lanka.",
  ];

  const stats = [
    { value: "200+", label: "Guides" },
    { value: "50+", label: "Destinations" },
    { value: "4.9★", label: "Rating" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <main className="flex-1 w-full">
        {/* ===== HERO — Full-width centered banner ===== */}
        <section className="bg-purple-900 pt-24 md:pt-30 pb-20 px-4 sm:px-6 text-center relative overflow-hidden">
          {" "}
          <div className="absolute top-0 left-0 w-72 h-72 bg-purple-800/40 rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-800/30 rounded-full translate-x-1/3 translate-y-1/3 pointer-events-none" />
          <div className="relative max-w-3xl mx-auto">
            <span className="inline-block bg-purple-700 text-purple-200 text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              🇱🇰 Expert Local Tour Guides
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight mb-5">
              Explore Sri Lanka with{" "}
              <span className="text-purple-400">Passionate</span> Local Guides
            </h1>
            <p className="text-purple-200 text-sm sm:text-base leading-relaxed mb-8 max-w-xl mx-auto">
              Discover the true essence of Sri Lanka through the eyes of
              licensed local guides who bring history, culture, and hidden gems
              to life.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => router.push("/tour-guide/sign-in")}
                className="bg-purple-500 hover:bg-purple-400 text-white font-semibold py-3 px-8 rounded-lg transition-colors text-sm cursor-pointer"
              >
                Sign In
              </button>
              <button
                onClick={() => router.push("/tour-guide/sign-up")}
                className="bg-transparent hover:bg-purple-700 text-purple-300 hover:text-white font-semibold py-3 px-8 rounded-lg border border-purple-500 transition-colors text-sm cursor-pointer"
              >
                Become a Guide
              </button>
            </div>
          </div>
        </section>

        {/* ===== STACKED FEATURE CARDS ===== */}
        <section className="bg-gray-50 py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Card 1 — Why Book */}
            <div className="bg-purple-50 dark:bg-purple-900 rounded-2xl border border-purple-100 overflow-hidden">
              <div className="flex flex-col sm:flex-row">
                <div className="w-full sm:w-2 bg-purple-500 flex-shrink-0" />
                <div className="p-7 sm:p-8 flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
                    <div className="flex-1">
                      <p className="text-purple-500 text-xs font-bold tracking-widest uppercase mb-2">
                        Why Book a Guide
                      </p>
                      <h2 className="text-xl font-bold text-gray-900 mb-1">
                        The best way to explore Sri Lanka
                      </h2>
                      <div className="w-8 h-0.5 bg-purple-400 rounded mb-4" />
                      <p className="text-gray-500 text-sm leading-relaxed mb-5">
                        Enhance your travel with a knowledgeable local guide.{" "}
                        <button
                          onClick={() => router.push("/tour-guides")}
                          className="text-purple-600 underline underline-offset-2 hover:text-purple-800 transition-colors cursor-pointer bg-transparent border-none p-0 text-sm"
                        >
                          Browse all tour guides
                        </button>{" "}
                        and find the perfect match for your journey.
                      </p>
                      <ul className="space-y-3">
                        {whyBookFeatures.map((item, i) => (
                          <CheckListItemLight key={i} text={item} />
                        ))}
                      </ul>
                    </div>
                    <div className="sm:text-right sm:flex-shrink-0">
                      <button
                        onClick={() => router.push("/tour-guides")}
                        className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2.5 px-6 rounded-lg transition-colors text-sm cursor-pointer whitespace-nowrap"
                      >
                        Browse Guides →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2 — Become a Guide */}
            <div className="bg-purple-900 rounded-2xl border border-purple-700 overflow-hidden">
              <div className="flex flex-col sm:flex-row">
                <div className="w-full sm:w-2 bg-purple-400 flex-shrink-0" />
                <div className="p-7 sm:p-8 flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
                    <div className="flex-1">
                      <p className="text-purple-400 text-xs font-bold tracking-widest uppercase mb-2">
                        Become a Guide
                      </p>
                      <h2 className="text-xl font-bold text-white mb-1">
                        Share your passion, earn income
                      </h2>
                      <div className="w-8 h-0.5 bg-purple-500 rounded mb-4" />
                      <p className="text-purple-200 text-sm leading-relaxed mb-5">
                        Are you a licensed guide with a passion for sharing Sri
                        Lanka's beauty? Turn your expertise into a rewarding
                        career and connect with travelers worldwide.
                      </p>
                      <ul className="space-y-3">
                        {guideBenefits.map((item, i) => (
                          <CheckListItemDark key={i} text={item} />
                        ))}
                      </ul>
                    </div>
                    <div className="sm:flex-shrink-0 flex flex-col gap-3">
                      <button
                        onClick={() => router.push("/tour-guide/sign-in")}
                        className="bg-purple-500 hover:bg-purple-400 text-white font-semibold py-2.5 px-6 rounded-lg transition-colors text-sm cursor-pointer whitespace-nowrap"
                      >
                        Sign In
                      </button>
                      <button
                        onClick={() => router.push("/tour-guide/sign-up")}
                        className="bg-transparent hover:bg-purple-700 text-purple-300 hover:text-white font-semibold py-2.5 px-6 rounded-lg border border-purple-500 transition-colors text-sm cursor-pointer whitespace-nowrap"
                      >
                        Become a Guide
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== IDENTITY BANNER ===== */}
        <section className="bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 text-center border-t border-gray-100">
          <div className="max-w-2xl mx-auto">
            <p className="text-purple-500 text-xs font-bold tracking-widest uppercase mb-3">
              🇱🇰 Our Identity
            </p>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              Built by Sri Lankans, for Sri Lankans, for Sri Lanka
            </h3>
            <p className="text-gray-500 text-sm leading-relaxed">
              Sri Lanka's first fully operational SLTDA-registered web &amp; app
              platform — connecting travelers with authentic local experiences.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}
